import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StageBadge from '../components/StageBadge';

// A sales agent's own deals: the ones they sold. Takes the place of the acquisitions Deals tab in
// their sidebar.
//
// Nothing here is owned until it is sold — while a deal is Available every sales agent markets it
// from the Pipeline. Moving it to Sold makes them its sales agent (deals.dispo_user_id) and its
// co-owner alongside the acquisitions agent, so it appears here with its money: GET /api/deals scopes
// "mine" to deals you acquired OR sold, and redacts per row, so the fee arrives only on these.

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  return Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : '—';
}
// Date-only values arrive as text; never through new Date(), which would read them as UTC midnight.
function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return (y && m && day) ? `${m}/${day}/${y}` : '—';
}

const TABS = [
  { key: 'sold',   label: 'Pending close' },
  { key: 'closed', label: 'Closed' },
];

export default function MySales() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('sold');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/deals?tab=${tab}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setRows(Array.isArray(d) ? d : (Array.isArray(d?.deals) ? d.deals : [])); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>My Sales</h1>
        <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>
          Deals you sold. To sell one, open an Available deal from the{' '}
          <span onClick={() => navigate('/pipeline')} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>Pipeline</span>
          {' '}and mark it Sold.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                     border: `1px solid ${tab === t.key ? 'var(--teal)' : 'var(--navy-border)'}`,
                     background: tab === t.key ? 'var(--teal)' : 'transparent',
                     color: tab === t.key ? '#fff' : 'var(--gray-300)' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
            {tab === 'sold' ? 'Nothing pending close.' : 'No closed sales yet.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr style={{ background: 'rgba(250, 247, 242,0.6)', borderBottom: '1px solid var(--navy-border)' }}>
                  {['Property', 'Stage', 'End Buyer', 'Sold Price', 'Fee', 'Close Date'].map(h => (
                    <th key={h} style={{ padding: '9px 16px', fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: ['Sold Price', 'Fee'].includes(h) ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} onClick={() => navigate(`/pipeline/deal/${r.id}`)}
                    style={{ borderBottom: i === rows.length - 1 ? 'none' : '1px solid rgba(232,227,218,0.5)', cursor: 'pointer' }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(20, 80, 74,0.04)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{r.address}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[r.city, r.state, r.zip].filter(Boolean).join(', ')}</div>
                    </td>
                    <td style={{ padding: '10px 16px' }}><StageBadge stage={r.stage} readOnly /></td>
                    <td style={{ padding: '10px 16px', fontSize: 12.5, color: 'var(--gray-300)' }}>{r.buyer_name || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--white)', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(r.sale_price)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--gold)', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(r.fee ?? r.assignment_fee)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{fmtDate(r.close_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
